import type { Vec2 } from './math';

/**
 * Centripetal Catmull-Rom interpolation.
 *
 * Given 4 control points P0..P3, returns the point on the spline between
 * P1 and P2 at parameter t ∈ [0, 1]. We use the uniform variant (alpha=0)
 * because:
 *   - it's the cheapest (no per-segment reparameterization),
 *   - our control points are already roughly evenly spaced by the chain
 *     constraint, so the visual difference vs centripetal is negligible.
 */
export const catmullRom = (
  p0: Vec2,
  p1: Vec2,
  p2: Vec2,
  p3: Vec2,
  t: number,
  out: Vec2 = { x: 0, y: 0 },
): Vec2 => {
  const t2 = t * t;
  const t3 = t2 * t;

  // Tangent-aware basis. Coefficients of a uniform Catmull-Rom expansion:
  //   P(t) = 0.5 * ( (2*P1) + (-P0 + P2)*t + (2P0 - 5P1 + 4P2 - P3)*t2 + (-P0 + 3P1 - 3P2 + P3)*t3 )
  out.x =
    0.5 *
    (2 * p1.x +
      (-p0.x + p2.x) * t +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
  out.y =
    0.5 *
    (2 * p1.y +
      (-p0.y + p2.y) * t +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
  return out;
};

/**
 * Sample a Catmull-Rom spline through `points` at `samplesPerSegment` density.
 * End-handles are duplicated so the curve passes through P0 and P_last.
 *
 * Returns a reused output buffer when provided — important: the engine calls
 * this every frame and we must not allocate.
 */
export const sampleSpline = (
  points: ReadonlyArray<Vec2>,
  samplesPerSegment: number,
  out: Vec2[],
): Vec2[] => {
  const n = points.length;
  if (n === 0) {
    out.length = 0;
    return out;
  }
  if (n === 1) {
    out.length = 1;
    if (!out[0]) out[0] = { x: 0, y: 0 };
    out[0].x = points[0]!.x;
    out[0].y = points[0]!.y;
    return out;
  }

  const expected = (n - 1) * samplesPerSegment + 1;
  while (out.length < expected) out.push({ x: 0, y: 0 });
  out.length = expected;

  let idx = 0;
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const last = i === n - 2;
    const steps = last ? samplesPerSegment + 1 : samplesPerSegment;
    for (let s = 0; s < steps; s++) {
      const t = s / samplesPerSegment;
      catmullRom(p0, p1, p2, p3, t, out[idx]!);
      idx++;
    }
  }
  return out;
};
